import { useState, type ReactNode } from "react";

import { styled } from "@linaria/react";

import Button, { ButtonVariant } from "@gx/dls/buttons/Button";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, { FlexDirection, JustifyContent } from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import TextInput from "@gx/dls/inputs/TextInput";
import Modal from "@gx/dls/modal/Modal";
import Code from "@gx/dls/text/Code";
import Selectable from "@gx/dls/text/Selectable";
import Text, { TextWeight } from "@gx/dls/text/Text";
import { PropsWithTheme, withTheme } from "@gx/dls/theme";

import BaseHeader from "@/layouts/components/BaseHeader";

const ModalWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: column;
  width: 500px;
  background-color: ${({ theme }) => theme.color.background.base};
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
  border-radius: 8px;
`);

const ModalBodyWrapper = withTheme(styled.div<PropsWithTheme>`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  padding: 16px;
`);

const ModalFooterWrapper = withTheme(styled.div<PropsWithTheme>`
  border-radius: 0 0 8px 8px;
  padding: 16px;
`);

export interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Consequences and context; wording is entirely up to the caller. */
  body: ReactNode;
  /** Exact string the user must type (shown in monospace and matched against the input). */
  confirmationPhrase: string;
  /** Primary action button label. */
  confirmLabel: string;
  isPending?: boolean;
  /** When false, skips the type-to-confirm field. */
  isConfirmationRequired?: boolean;
}

const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmationPhrase,
  confirmLabel,
  isPending = false,
  isConfirmationRequired = true,
}: DeleteConfirmDialogProps) => {
  const [confirmationText, setConfirmationText] = useState("");

  const isConfirmationValid = !isConfirmationRequired || confirmationText === confirmationPhrase;

  const handleClose = () => {
    setConfirmationText("");
    onClose();
  };

  const handleConfirm = () => {
    if (isConfirmationValid) {
      onConfirm();
      setConfirmationText("");
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalWrapper>
        <FlexWrapper padding="16px" fillWidth>
          <BaseHeader title={title} onClose={handleClose} />
        </FlexWrapper>
        <FlexItem shrink={0} grow={0}>
          <HorizontalDivider />
        </FlexItem>
        <ModalBodyWrapper>
          <FlexWrapper direction={FlexDirection.COLUMN} gap={16}>
            {body}
            {isConfirmationRequired ? (
              <>
                <Text weight={TextWeight.MEDIUM}>
                  Please type{" "}
                  <Code>
                    <Selectable>{confirmationPhrase}</Selectable>
                  </Code>{" "}
                  to confirm.
                </Text>
                <TextInput
                  value={confirmationText}
                  onChange={setConfirmationText}
                  placeholder={confirmationPhrase}
                  fillWidth
                />
              </>
            ) : null}
          </FlexWrapper>
        </ModalBodyWrapper>
        <HorizontalDivider />
        <ModalFooterWrapper>
          <FlexWrapper justifyContent={JustifyContent.END} gap={12}>
            <Button
              label="Cancel"
              variant={ButtonVariant.SECONDARY}
              onClick={handleClose}
              isDisabled={isPending}
              fillWidth
            />
            <Button
              label={confirmLabel}
              variant={ButtonVariant.ERROR}
              onClick={handleConfirm}
              isDisabled={isPending || !isConfirmationValid}
              fillWidth
            />
          </FlexWrapper>
        </ModalFooterWrapper>
      </ModalWrapper>
    </Modal>
  );
};

export default DeleteConfirmDialog;
